import React, { useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, MonitorIcon, SmartphoneIcon, PaletteIcon, LayoutGridIcon, SparklesIcon, Undo2Icon, Redo2Icon, CheckIcon, EyeIcon, PencilIcon, ExternalLinkIcon } from 'lucide-react';
import { StorefrontViewport, PreviewDevice, PreviewZoom } from './StorefrontViewport';
import { ShopDesign } from './sections/ShopDesign';
import { ShopContent } from './sections/ShopContent';
import { AiExperience } from './sections/AiExperience';
import { ShopConfig } from '../../data/shopConfig';
export type StudioTab = 'design' | 'content' | 'ai';
const TABS: {
  id: StudioTab;
  label: string;
  icon: React.ElementType;
}[] = [{
  id: 'design',
  label: 'Design',
  icon: PaletteIcon
}, {
  id: 'content',
  label: 'Content',
  icon: LayoutGridIcon
}, {
  id: 'ai',
  label: 'AI',
  icon: SparklesIcon
}];
interface ShopStudioProps {
  config: ShopConfig;
  update: (patch: Partial<ShopConfig>) => void;
  initialTab: StudioTab;
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  onResetTheme: () => void;
  /** Guided setup shows Back / Save & Continue instead of Done. */
  onboarding?: boolean;
  step?: number;
  totalSteps?: number;
  onBack?: () => void;
  onNext?: () => void;
  onClose: () => void;
  onSave: () => void;
  /** What the back button returns to — "Settings" or "Studio". */
  backLabel?: string;
}
/**
 * Shop Studio — the shop fills the workspace and the controls live in a panel
 * beside it. Nothing about the page scrolls: the storefront scrolls inside its
 * own viewport and the panel scrolls independently, so the preview is always
 * in front of the merchant no matter how far they get through the options.
 */
export function ShopStudio({
  config,
  update,
  initialTab,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  onResetTheme,
  onboarding,
  step,
  totalSteps,
  onBack,
  onNext,
  onClose,
  onSave,
  backLabel = 'Settings'
}: ShopStudioProps) {
  const [tab, setTab] = useState<StudioTab>(initialTab);
  const [device, setDevice] = useState<PreviewDevice>('desktop');
  const [zoom, setZoom] = useState<PreviewZoom>('fit');
  // Small screens can't show both at once, so they flip between them.
  const [mobileView, setMobileView] = useState<'edit' | 'preview'>('edit');
  const panel = tab === 'design' ? <ShopDesign config={config} update={update} canUndo={canUndo} canRedo={canRedo} onUndo={onUndo} onRedo={onRedo} onResetTheme={onResetTheme} compact data-id="element-4422" data-mp="27otl2okr1ie0" /> : tab === 'content' ? <ShopContent config={config} update={update} compact data-id="element-4423" data-mp="2vj4r6mb21ilm" /> : <AiExperience config={config} update={update} compact data-id="element-4424" data-mp="2qluseg1rzul4" />;
  return <div className="fixed inset-0 z-[60] bg-slate-100 flex flex-col" data-id="element-4425" data-mp="3o0qjai92xqgp">
      {/* Studio toolbar — Business Hub chrome, never part of the shop */}
      <header className="flex-shrink-0 flex items-center gap-3 px-3 lg:px-5 h-14 bg-white border-b border-slate-200" data-id="element-4426" data-mp="3j8z0wv02ur1n">
        <button onClick={onClose} className="inline-flex items-center gap-1.5 px-2.5 py-2 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-50 hover:text-primary-indigo transition-colors duration-150 ease-out" data-id="element-4427" data-mp="3qmpw8bvs4d8f">
          <ChevronLeftIcon className="w-4 h-4" data-id="element-4428" data-mp="9dkrq3i5jvop" />
          <span className="hidden sm:inline" data-id="element-4429" data-mp="9ecmxd7u7reg">{backLabel}</span>
        </button>

        <div className="w-px h-6 bg-slate-200 hidden sm:block" data-id="element-4430" data-mp="ddv726ru3yg8" />

        <div className="min-w-0 hidden sm:block" data-id="element-4431" data-mp="id7twriph5it">
          <p className="text-sm font-bold text-dark-navy truncate leading-tight" data-id="element-4432" data-mp="ibwrav0zy3ta">
            Shop Studio
          </p>
          <p className="text-[11px] text-slate-400 truncate leading-tight" data-id="element-4433" data-mp="n6sjbv68gr6e">
            {onboarding && step ? `Step ${step} of ${totalSteps} · talab.app/shop/${config.handle}` : `talab.app/shop/${config.handle}`}
          </p>
        </div>

        {/* Viewport controls */}
        <div className="ml-auto flex items-center gap-1.5 lg:gap-2" data-id="element-4434" data-mp="111uvh01obcrn">
          <div className="hidden lg:inline-flex p-1 bg-slate-100 rounded-xl" data-id="element-4435" data-mp="1yriy6y1uuomx">
            {[{
            id: 'desktop' as PreviewDevice,
            label: 'Desktop',
            icon: MonitorIcon
          }, {
            id: 'mobile' as PreviewDevice,
            label: 'Mobile',
            icon: SmartphoneIcon
          }].map(d => <button key={d.id} onClick={() => setDevice(d.id)} className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-colors duration-150 ease-out ${device === d.id ? 'bg-white text-dark-navy shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-4436" data-mp="1aw6jd2i0ae0r">
                <d.icon className="w-3.5 h-3.5" data-id="element-4437" data-mp="2vb0591aqh8e9" />
                {d.label}
              </button>)}
          </div>

          <div className="hidden lg:inline-flex p-1 bg-slate-100 rounded-xl" data-id="element-4438" data-mp="33rqz07gg1uqj">
            {(['fit', '75', '100'] as PreviewZoom[]).map(z => <button key={z} onClick={() => setZoom(z)} className={`px-2 py-1.5 rounded-lg text-xs font-semibold transition-colors duration-150 ease-out ${zoom === z ? 'bg-white text-dark-navy shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-4439" data-mp="37rhom7u8i4th">
                {z === 'fit' ? 'Fit' : `${z}%`}
              </button>)}
          </div>

          <div className="hidden sm:flex items-center gap-1" data-id="element-4440" data-mp="3rjec9mks97ac">
            <button onClick={onUndo} disabled={!canUndo} aria-label="Undo" className="p-2 rounded-lg text-slate-500 hover:text-primary-indigo hover:bg-slate-50 disabled:opacity-40 transition-colors duration-150 ease-out" data-id="element-4441" data-mp="3cs5ntgsm9rys">
              <Undo2Icon className="w-4 h-4" data-id="element-4442" data-mp="e3gl3yc85vaz" />
            </button>
            <button onClick={onRedo} disabled={!canRedo} aria-label="Redo" className="p-2 rounded-lg text-slate-500 hover:text-primary-indigo hover:bg-slate-50 disabled:opacity-40 transition-colors duration-150 ease-out" data-id="element-4443" data-mp="nz71yty0myay">
              <Redo2Icon className="w-4 h-4" data-id="element-4444" data-mp="9z5yr7ua3esb" />
            </button>
          </div>

          {/* Mobile flip between shop and controls */}
          <button onClick={() => setMobileView(v => v === 'edit' ? 'preview' : 'edit')} className="lg:hidden inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-100 text-xs font-semibold text-slate-600" data-id="element-4445" data-mp="50kyx9ixux4e">
            {mobileView === 'edit' ? <>
                <EyeIcon className="w-3.5 h-3.5" data-id="element-4446" data-mp="3megevzhcut6m" />
                Preview
              </> : <>
                <PencilIcon className="w-3.5 h-3.5" data-id="element-4447" data-mp="3df33v2dl1wjs" />
                Edit
              </>}
          </button>

          {onboarding ? <div className="flex items-center gap-2" data-id="element-4448" data-mp="1szogoskdkl3w">
              <button onClick={onBack} className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm font-semibold text-slate-600 hover:border-slate-300 transition-colors duration-150 ease-out" data-id="element-4449" data-mp="1xvvo6psf64ny">
                <ChevronLeftIcon className="w-4 h-4" data-id="element-4450" data-mp="1a7vov1h1l5xr" />
                Back
              </button>
              <button onClick={onNext} className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary-indigo text-white rounded-xl text-sm font-semibold hover:bg-indigo-700 transition-colors duration-150 ease-out" data-id="element-4451" data-mp="1e7we7h5oj1jl">
                Save &amp; Continue
                <ChevronRightIcon className="w-4 h-4" data-id="element-4452" data-mp="qjv80evz3x7c" />
              </button>
            </div> : <button onClick={onSave} className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary-indigo text-white rounded-xl text-sm font-semibold hover:bg-indigo-700 transition-colors duration-150 ease-out" data-id="element-4453" data-mp="vh4xfujd48v6">
              <CheckIcon className="w-4 h-4" data-id="element-4454" data-mp="3u0y9ioxm963e" />
              <span className="hidden sm:inline" data-id="element-4455" data-mp="3u1ptt3lrjhed">Save</span>
            </button>}
        </div>
      </header>

      <div className="flex-1 min-h-0 flex" data-id="element-4456" data-mp="3py1j4c1u45ow">
        {/* The shop, as large as the workspace allows */}
        <main className={`flex-1 min-w-0 p-3 lg:p-5 ${mobileView === 'preview' ? 'block' : 'hidden lg:block'}`} data-id="element-4457" data-mp="7rvig63lp328">
          <div className="w-full h-full rounded-2xl bg-white border border-slate-200 shadow-[0_12px_40px_rgba(11,18,32,0.08)] overflow-hidden flex flex-col" data-id="element-4458" data-mp="hj9sh7t6hl9m">
            <div className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 border-b border-slate-100 bg-slate-50" data-id="element-4459" data-mp="cl88mknxhhx3">
              <span className="w-2.5 h-2.5 rounded-full bg-slate-300" data-id="element-4460" data-mp="qhv5knfvs830" />
              <span className="w-2.5 h-2.5 rounded-full bg-slate-300" data-id="element-4461" data-mp="lmpoc1xfbhl9" />
              <span className="w-2.5 h-2.5 rounded-full bg-slate-300" data-id="element-4462" data-mp="156n0xpxyv8m1" />
              <span className="mx-auto text-[11px] text-slate-400 truncate" data-id="element-4463" data-mp="1a5rcgn8d5ifj">
                talab.app/shop/{config.handle}
              </span>
              <a href={`/shop/${config.handle}`} target="_blank" rel="noreferrer" title="Open full preview" className="p-1 rounded text-slate-400 hover:text-primary-indigo transition-colors duration-150 ease-out" data-id="element-4464" data-mp="w5gkfgvbhxss">
                <ExternalLinkIcon className="w-3.5 h-3.5" data-id="element-4465" data-mp="r8q894mmvmcn" />
              </a>
            </div>
            <StorefrontViewport config={config} device={device} zoom={zoom} className="flex-1 min-h-0" data-id="element-4466" data-mp="cjtzf5cr65az" />
          </div>
        </main>

        {/* Controls panel — the only thing that scrolls */}
        <aside className={`w-full lg:w-[430px] xl:w-[470px] flex-shrink-0 bg-white lg:border-l border-slate-200 flex flex-col min-h-0 ${mobileView === 'edit' ? 'flex' : 'hidden lg:flex'}`} data-id="element-4467" data-mp="8h5sj37ln8gn">
          <div className="flex-shrink-0 p-3 border-b border-slate-100" data-id="element-4468" data-mp="10418dmpsd045">
            <div className="flex p-1 bg-slate-100 rounded-xl" data-id="element-4469" data-mp="m49oj5cl8pi8">
              {TABS.map(t => {
              const Icon = t.icon;
              const isActive = t.id === tab;
              return <button key={t.id} onClick={() => setTab(t.id)} className={`flex-1 inline-flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-[13px] font-semibold transition-colors duration-150 ease-out ${isActive ? 'bg-white text-primary-indigo shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-4470" data-mp="1es5kg7uc2ddy">
                    <Icon className="w-4 h-4" data-id="element-4471" data-mp="1xqzzlhxplfun" />
                    {t.label}
                  </button>;
            })}
            </div>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto p-4 lg:p-5" data-id="element-4472" data-mp="22k2vvxtvf3ej">
            {panel}
          </div>
        </aside>
      </div>
    </div>;
}